import { crmFetch, dataOf } from '../chat/api'
import type { CrmAccount } from '../dynamicLists/types'
import type { CostPayment, Earning, LinkDetail, LinkKind, LinkReport, ListQuery, ListResult, Range, ReportDraft, TrialDraft } from './types'

/** What the Tracking links page calls (CRM routes, super admin only); tests pass fakes. */
export interface TrackingLinksApi {
  accounts: () => Promise<CrmAccount[]>
  list: (q: ListQuery) => Promise<ListResult>
  /** Every row of the view as CSV (not one page). */
  exportCsv: (q: ListQuery) => Promise<{ filename: string; csv: string }>
  detail: (accountId: string, kind: LinkKind, linkId: string, range: Range, earning: Earning) => Promise<LinkDetail>
  /** Ask the worker to re-read the Account's links from OnlyFans. */
  refresh: (accountId: string) => Promise<{ requested_at: string | null }>
  create: (accountId: string, name: string, cost: { amount: number } | null) => Promise<{ link: { link_id: string; name: string } }>
  createTrial: (accountId: string, draft: TrialDraft) => Promise<{ link: { link_id: string; name: string } }>
  archive: (accountId: string, kind: LinkKind, linkId: string, archived: boolean) => Promise<{ archived: boolean }>
  addCost: (accountId: string, kind: LinkKind, linkId: string, amount: number, paidOn: string, note: string | null) => Promise<{ cost: CostPayment }>
  deleteCost: (id: string) => Promise<unknown>
  reports: () => Promise<{ reports: LinkReport[]; public_base: string | null }>
  createReport: (draft: ReportDraft) => Promise<{ report: LinkReport }>
  updateReport: (id: string, draft: Partial<ReportDraft>) => Promise<{ report: LinkReport }>
  revokeReport: (id: string) => Promise<unknown>
}

async function call<T>(path: string, body: Record<string, unknown> | null, accountId = ''): Promise<T> {
  const res = await crmFetch(path, body ? { accountId, local: true, body } : { accountId, local: true, method: 'GET' })
  return dataOf<T>(res)
}

const listBody = (q: ListQuery) => ({
  kind: q.kind,
  ...(q.accountId ? { account_id: q.accountId } : {}),
  q: q.search || null,
  include_deleted: Boolean(q.includeDeleted),
  archived: q.archived ?? false,
  from: q.range?.from ?? null,
  to: q.range?.to ?? null,
  earning: q.earning,
  sort: q.sort,
  dir: q.dir,
  limit: q.limit ?? 100,
  offset: q.offset ?? 0,
})

export const trackingLinksApi: TrackingLinksApi = {
  accounts: async () => (await call<{ list: CrmAccount[] }>('/v1/accounts', null)).list,
  list: (q) => call('/v1/tracking-links/list', listBody(q)),
  exportCsv: (q) => call('/v1/tracking-links/export', listBody(q)),
  detail: (accountId, kind, linkId, range, earning) =>
    call('/v1/tracking-links/detail', { account_id: accountId, kind, link_id: linkId, from: range.from, to: range.to, earning }, accountId),
  refresh: (accountId) => call('/v1/tracking-links/refresh', { account_id: accountId }, accountId),
  create: (accountId, name, cost) => call('/v1/tracking-links/create', { account_id: accountId, name, ...(cost ? { cost } : {}) }, accountId),
  createTrial: (accountId, d) =>
    call(
      '/v1/tracking-links/create-trial',
      {
        account_id: accountId,
        name: d.name,
        trial_days: d.trialDays,
        claims_cap: d.claimsCap,
        ...(d.cost ? { cost: d.cost } : {}),
      },
      accountId,
    ),
  archive: (accountId, kind, linkId, archived) =>
    call('/v1/tracking-links/archive', { account_id: accountId, kind, link_id: linkId, archived }, accountId),
  addCost: (accountId, kind, linkId, amount, paidOn, note) =>
    call('/v1/tracking-links/costs/add', { account_id: accountId, kind, link_id: linkId, amount, paid_on: paidOn, note }, accountId),
  deleteCost: (id) => call('/v1/tracking-links/costs/delete', { id }),
  reports: () => call('/v1/link-reports/list', {}),
  createReport: (draft) => call('/v1/link-reports/create', { ...draft }),
  updateReport: (id, draft) => call('/v1/link-reports/update', { id, ...draft }),
  revokeReport: (id) => call('/v1/link-reports/revoke', { id }),
}
